import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Divider, ConfigProvider } from "antd";
import { LogoutOutlined, BellOutlined } from "@ant-design/icons";
import { ToastContainer } from 'react-toastify';
import { logout } from './services/api';
import CustomersPage from "./pages/customer/CustomersPage";
import OrdersPage from "./pages/orders/OrdersPage";
import OrderItemsPage from "./pages/orderItems/OrderItemsPage";
import LoginPage from "./pages/login/LoginPage";
import AdminLoginPage from "./pages/login/admin/AdminLoginPage";
import AllOrdersPage from "./pages/orders/admin/AdminOrdersPage";
import AdminOrderItemsPage from "./pages/orderItems/admin/AdminOrderItemsPage";
import SidebarMenu from "./components/sidebar/SidebarMenu";
import EmployeesPage from "./pages/employee/EmployeesPage";
import EmployeeLoginPage from "./pages/login/employee/EmployeeLoginPage";
import EmployeeOrderItemsPage from "./pages/orderItems/employee/EmployeeOrderItemsPage";
import AdminDashboardPage from "./pages/dashboard/admin/AdminDashboardPage";
import ErrorBoundary from './components/ErrorBoundary';
import NotificationPreferencesPage from "./pages/settings/NotificationPreferencesPage";
import AuditLogsPage from "./pages/audit/AuditLogsPage";
import "antd/dist/reset.css";
import "./styles/index.css";
import 'react-toastify/dist/ReactToastify.css';
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/legal/TermsOfServicePage";
import OwnerCardPage from "./pages/ownerCard/OwnerCardPage";
import PublicOrderDetailsPage from "./pages/public/PublicOrderDetails";
import ProductsPage from "./pages/products/ProductsPage";
import AdminInvoicesPage from "./pages/invoices/admin/AdminInvoicesPage";
import InvoiceDetailPage from "./pages/invoices/admin/InvoiceDetailPage";
import InvoiceSettingsPage from "./pages/invoiceSettings/InvoiceSettingsPage";
import CustomerInvoicesPage from "./pages/invoices/customer/CustomerInvoicesPage";

const { Header, Content, Footer, Sider } = Layout;
const { Title, Text } = Typography;

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
};

const PrivateAdminRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const userType = localStorage.getItem("userType");

  if (!token) {
    console.log("No token found");
    return <Navigate to="/admin/login" />;
  }

  if (userType !== "ADMIN" && userType !== "SUPER_ADMIN") {
    console.log("Access denied: user is a CUSTOMER");
    return <Navigate to="/login" />;
  }

  return children;
};

const EmployeeRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const userType = localStorage.getItem("userType");

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (userType !== "EMPLOYEE") {
    console.log(`Access denied: user is ${userType}`);

    if (userType === "CUSTOMER") {
      return <Navigate to="/dashboard" />;
    } else if (userType === "ADMIN") {
      return <Navigate to="/admin/dashboard" />;
    } else {
      return <Navigate to="/login" />;
    }
  }
  return children;
}

const AppContent = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login" || location.pathname === "/admin/login" || location.pathname === "/employee/login" || location.pathname === "/order-query";

  return (
    <Content className={isLoginPage ? "login-content" : "app-content"}>
      <div className="content-container">
        <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/employee/login" element={<EmployeeLoginPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
            <Route path="/kullanim-sartlari" element={<TermsOfServicePage />} />
            <Route path="/order-query" element={<PublicOrderDetailsPage />} />
            <Route
              path="/admin/customers"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <CustomersPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/employees"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <EmployeesPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <AdminDashboardPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/owner-card"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <OwnerCardPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <AuditLogsPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <ProductsPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <AdminInvoicesPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/invoices/:id"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <InvoiceDetailPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/invoice-settings"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <InvoiceSettingsPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute>
                  <ErrorBoundary>
                    <OrdersPage />
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/order-items/:orderId"
              element={
                <PrivateRoute>
                  <ErrorBoundary>
                    <OrderItemsPage />
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <PrivateRoute>
                  <ErrorBoundary>
                    <CustomerInvoicesPage />
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <AllOrdersPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/admin/order-items/:orderId"
              element={
                <PrivateAdminRoute>
                  <ErrorBoundary>
                    <AdminOrderItemsPage />
                  </ErrorBoundary>
                </PrivateAdminRoute>
              }
            />
            <Route
              path="/employee/order-items"
              element={
                <EmployeeRoute>
                  <ErrorBoundary>
                    <EmployeeOrderItemsPage />
                  </ErrorBoundary>
                </EmployeeRoute>
              }
            />
            <Route
              path="/settings/notifications"
              element={
                <PrivateRoute>
                  <ErrorBoundary>
                    <NotificationPreferencesPage />
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/login" />} />
          </Routes>
        </ErrorBoundary>
      </div>
    </Content>
  );
};

const AppFooter = () => {
  const token = localStorage.getItem("token");

  if (!token) return null;

  return (
    <Footer className="app-footer">
      © 2025 Siparify | Sipariş Takip Sistemi v1.0.0
      <Divider type="vertical" />
      <a href="/gizlilik-politikasi">Gizlilik Politikası</a>
      <Divider type="vertical" />
      <a href="/kullanim-sartlari">Kullanım Şartları</a>
    </Footer>
  );
};

const App = () => {
  const token = localStorage.getItem("token");
  const userName = localStorage.getItem("userName");

  const handleLogout = async () => {
    await logout();
  };

  const userMenu = (
    <Menu className="user-dropdown-menu">
      <div className="user-dropdown-header">
        <Avatar size={48} style={{ backgroundColor: '#2563eb' }}>
          {userName?.charAt(0)}
        </Avatar>
        <div className="user-dropdown-info">
          <Text strong>{userName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {localStorage.getItem("userType") === "USER" ? "Yönetici" : "Müşteri"}
          </Text>
        </div>
      </div>
      <Menu.Divider />
      <Menu.Item key="notifications" icon={<BellOutlined />}>
        <a href="/settings/notifications">Bildirim Tercihleri</a>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Çıkış
      </Menu.Item>
    </Menu>
  );

  return (
    <ErrorBoundary>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#2563eb',
            colorSuccess: '#059669',
            colorWarning: '#d97706',
            colorError: '#dc2626',
            colorInfo: '#2563eb',
            colorText: '#0f172a',
            colorTextSecondary: '#475569',
            colorBorder: '#e2e8f0',
            colorBorderSecondary: '#f1f5f9',
            colorBgContainer: '#ffffff',
            colorBgLayout: '#f9fafb',
            colorBgElevated: '#ffffff',
            borderRadius: 8,
            borderRadiusLG: 8,
            borderRadiusSM: 6,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: 14,
            fontSizeLG: 16,
            fontSizeSM: 13,
            lineHeight: 1.5,
            controlHeight: 40,
            controlHeightLG: 48,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            boxShadowSecondary: '0 4px 16px rgba(0,0,0,0.06)',
            wireframe: false,
          },
          components: {
            Menu: {
              darkItemBg: '#111827',
              darkSubMenuItemBg: '#1f2937',
              darkItemColor: '#9ca3af',
              darkItemHoverBg: 'rgba(255,255,255,0.06)',
              darkItemSelectedBg: 'rgba(255,255,255,0.10)',
              darkItemSelectedColor: '#f9fafb',
              itemBorderRadius: 6,
            },
            Button: {
              borderRadius: 6,
              fontWeight: 600,
            },
            Table: {
              headerBg: '#f1f5f9',
              rowHoverBg: '#f7f9fa',
              borderColor: '#e1eaef',
              headerColor: '#536471',
            },
            Card: {
              borderRadiusLG: 8,
            },
            Modal: {
              borderRadiusLG: 8,
            },
            Input: {
              borderRadius: 8,
              activeShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
            },
            Select: {
              borderRadius: 8,
            },
          },
        }}
      >
      <Router>
        <Layout style={{ minHeight: '100vh' }}>
          {token && (
            <ErrorBoundary>
              <Sider
                breakpoint="lg"
                collapsedWidth="0"
                theme="dark"
                width={240}
                className="app-sider"
              >
                <SidebarMenu />
              </Sider>
            </ErrorBoundary>
          )}
          <Layout>
            {token && (
              <ErrorBoundary>
                <Header className="app-header">
                  <div className="header-logo">
                    <img src="/img/icon-512.png" width="120" height="35" alt="Siparify" />
                    <Title level={4}>Siparify</Title>
                  </div>
                  <div className="header-actions">
                    <a href="/settings/notifications" className="notification-icon">
                      <BellOutlined style={{ fontSize: '18px' }} />
                    </a>
                    <Divider type="vertical" />
                    <Dropdown overlay={userMenu} trigger={['click']} placement="bottomRight">
                      <a onClick={e => e.preventDefault()} className="user-dropdown-trigger">
                        <Space>
                          <Avatar style={{ backgroundColor: '#2563eb' }}>
                            {userName?.charAt(0)}
                          </Avatar>
                          <span className="d-none d-md-inline username-text">
                            {userName}
                          </span>
                        </Space>
                      </a>
                    </Dropdown>
                  </div>
                </Header>
              </ErrorBoundary>
            )}
            <AppContent />
            <AppFooter />
          </Layout>
        </Layout>

        {/* Toast Container for notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Router>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default App;
